import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useApp } from "@/contexts/AppContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, Search } from "lucide-react";

const TIPOS_DOC = ["RUC", "DNI", "CE", "PAS"];

export default function Clientes() {
  const { empresa, formatApiError } = useApp();
  const [items, setItems] = useState([]);
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const blank = { tipo_doc: "RUC", numero_doc: "", razon_social: "", direccion: "", telefono: "", email: "" };
  const [form, setForm] = useState(blank);

  const load = async () => {
    const { data } = await api.get(`/clientes${q ? `?q=${encodeURIComponent(q)}` : ""}`);
    setItems(data);
  };
  useEffect(() => { if (empresa) load(); /* eslint-disable-next-line */ }, [empresa, q]);

  const submit = async (e) => {
    e.preventDefault();
    try {
      if (editing) await api.put(`/clientes/${editing.id}`, form);
      else await api.post("/clientes", form);
      toast.success("Guardado");
      setOpen(false); setEditing(null); setForm(blank); load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const editar = (c) => { setEditing(c); setForm(c); setOpen(true); };
  const eliminar = async (c) => {
    if (!confirm(`¿Eliminar ${c.razon_social}?`)) return;
    await api.delete(`/clientes/${c.id}`); toast.success("Eliminado"); load();
  };

  return (
    <div className="space-y-5">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold tracking-tight text-slate-900">Clientes</h1>
          <p className="text-[13px] text-slate-500">Padrón de clientes para emisión de comprobantes.</p>
        </div>
        <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (!v) { setEditing(null); setForm(blank); } }}>
          <DialogTrigger asChild>
            <Button data-testid="btn-nuevo-cliente" className="h-9 bg-slate-900 hover:bg-slate-800 text-white">
              <Plus className="w-4 h-4 mr-1" /> Nuevo cliente
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-xl">
            <DialogHeader><DialogTitle>{editing ? "Editar cliente" : "Nuevo cliente"}</DialogTitle></DialogHeader>
            <form onSubmit={submit} className="grid grid-cols-2 gap-3 text-sm">
              <div>
                <Label className="text-[12px]">Tipo de documento</Label>
                <Select value={form.tipo_doc} onValueChange={(v) => setForm({ ...form, tipo_doc: v })}>
                  <SelectTrigger className="h-9"><SelectValue /></SelectTrigger>
                  <SelectContent>{TIPOS_DOC.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-[12px]">N° documento</Label>
                <Input data-testid="cli-doc" value={form.numero_doc} onChange={(e) => setForm({ ...form, numero_doc: e.target.value })} required className="h-9 mono" />
              </div>
              <div className="col-span-2">
                <Label className="text-[12px]">Razón social / Nombre</Label>
                <Input data-testid="cli-nombre" value={form.razon_social} onChange={(e) => setForm({ ...form, razon_social: e.target.value })} required className="h-9" />
              </div>
              <div className="col-span-2">
                <Label className="text-[12px]">Dirección</Label>
                <Input value={form.direccion} onChange={(e) => setForm({ ...form, direccion: e.target.value })} className="h-9" />
              </div>
              <div>
                <Label className="text-[12px]">Teléfono</Label>
                <Input value={form.telefono} onChange={(e) => setForm({ ...form, telefono: e.target.value })} className="h-9" />
              </div>
              <div>
                <Label className="text-[12px]">Email</Label>
                <Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="h-9" />
              </div>
              <DialogFooter className="col-span-2"><Button data-testid="cli-submit" type="submit" className="bg-slate-900 text-white">Guardar</Button></DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      <div className="bg-white border border-slate-200 rounded-md">
        <div className="p-3 border-b border-slate-200">
          <div className="relative max-w-md">
            <Search className="w-3.5 h-3.5 absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400" />
            <Input data-testid="cli-search" placeholder="Buscar por nombre o documento..." value={q} onChange={(e) => setQ(e.target.value)} className="h-9 pl-8" />
          </div>
        </div>
        <table className="tbl w-full">
          <thead><tr><th>Tipo</th><th>N° Doc</th><th>Razón social</th><th>Dirección</th><th>Teléfono</th><th>Email</th><th></th></tr></thead>
          <tbody>
            {items.length === 0 ? (
              <tr><td colSpan={7} className="text-center py-6 text-slate-500">Sin clientes registrados</td></tr>
            ) : items.map((c) => (
              <tr key={c.id}>
                <td><span className="badge badge-muted">{c.tipo_doc}</span></td>
                <td className="mono">{c.numero_doc}</td>
                <td className="font-medium text-slate-800">{c.razon_social}</td>
                <td className="text-slate-600">{c.direccion}</td>
                <td className="mono">{c.telefono}</td>
                <td>{c.email}</td>
                <td className="text-right whitespace-nowrap">
                  <button onClick={() => editar(c)} className="text-slate-600 hover:text-blue-600 mr-2"><Pencil className="w-4 h-4" /></button>
                  <button onClick={() => eliminar(c)} className="text-slate-600 hover:text-rose-600"><Trash2 className="w-4 h-4" /></button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
